import React, { useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Typography, Alert, Spin, Card, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useOnboardingStore } from '../stores/onboarding-store';
import OnboardingWizard from '../components/OnboardingWizard';
import IndividualOnboardingForm from '../components/IndividualOnboardingForm';
import InlineFieldGroups from '../components/InlineFieldGroups';
import useCustomerCreation from '../hooks/useCustomerCreation';
import { OnboardingStep } from '../types';
import {useGetCategoryById} from "@/shared/api/client/customer-category-controller";
import {useGetFieldConfigurationForCategoryGrouped} from "@/shared/api/client/field-configuration-controller";
import {CustomerType} from "@/shared/api/model";

const CUSTINFO_FIELDS = new Set(['ctitle', 'marital', 'housing', 'educatio', 'language', 'income', 'officer', 'kin1', 'kin2', 'relationshipManager']);

const { Title, Paragraph } = Typography;

const IndividualOnboardingView: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  
  const {
    selectedCategory,
    setSelectedCategory,
    currentStep,
    steps,
    setSteps,
    setCurrentStep,
    resetOnboarding,
    getCompletionPercentage,
    formData,
  } = useOnboardingStore();

  const { createIndividualCustomer, isSubmitting } = useCustomerCreation();

  const {
    data: category,
    isLoading: categoryLoading,
    error: categoryError,
  } = useGetCategoryById(Number(categoryId), {
    query: {
      enabled: !!categoryId,
    },
  });


  const {
    data: fieldConfiguration,
    isLoading: fieldsLoading,
    error: fieldsError,
  } = useGetFieldConfigurationForCategoryGrouped(Number(categoryId), {
    query: {
      enabled: !!categoryId,
    },
  });

  // Get UI configuration from category
  const uiConfig = category?.uiConfiguration;
  const renderingMode = uiConfig?.renderingMode || 'wizard'; // 'wizard', 'tabs', or 'inline'
  const wizardOrientation = uiConfig?.wizardOrientation || 'horizontal'; // 'horizontal' or 'vertical'
  const showProgress = uiConfig?.showProgress !== false; // Default to true

  // Debug logging
  console.log('=== ONBOARDING UI CONFIG ===');
  console.log('Full uiConfig:', uiConfig);
  console.log('Rendering Mode:', renderingMode);
  console.log('Wizard Orientation:', wizardOrientation);
  console.log('Show Progress:', showProgress);
  console.log('===========================');


  useEffect(() => {
    if (category && (!selectedCategory || selectedCategory.id !== category.id)) {
      setSelectedCategory(category);
    }
  }, [category, selectedCategory, setSelectedCategory]);

  useEffect(() => {
    if (fieldConfiguration && fieldConfiguration.fieldGroups) {
      // Convert field groups to onboarding steps
      const fieldGroupSteps: OnboardingStep[] = fieldConfiguration.fieldGroups
        .filter(group => group.isVisible)
        .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
        .map((group, index) => ({
          id: group.groupKey ?? `step-${index}`,
          title: group.groupName ?? '',
          description: group.description || `Complete ${group.groupName} information`,
          component: 'FieldGroup',
          isRequired: group.fields?.some(field => field.defaultRequired) || false,
          isCompleted: false,
          fieldGroup: group, // Store the field group data for rendering
        }));

      // Add additional steps for products, locations, documents, review
      // Note: Photo & Signature are now part of the "Biometric Information" field group
      const additionalSteps: OnboardingStep[] = [
        {
          id: 'products',
          title: 'Product Selection',
          description: 'Select products and services for the customer',
          component: 'ProductSelectionForm',
          isRequired: true,
          isCompleted: false,
        },
        {
          id: 'locations',
          title: 'Address Information',
          description: 'Customer address and location details',
          component: 'LocationForm',
          isRequired: true,
          isCompleted: false,
        },
        {
          id: 'documents',
          title: 'Document Upload',
          description: 'Upload required documents',
          component: 'DocumentUploadForm',
          isRequired: true,
          isCompleted: false,
        },
        {
          id: 'review',
          title: 'Review & Submit',
          description: 'Review all information before submission',
          component: 'ReviewForm',
          isRequired: true,
          isCompleted: false,
        },
      ];

      const allSteps = [...fieldGroupSteps, ...additionalSteps];
      setSteps(allSteps);
    }
  }, [fieldConfiguration, setSteps]);

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      // Mark current step as completed before moving to next
      const currentStepData = steps[currentStep];
      if (currentStepData && !currentStepData.isCompleted) {
        // Auto-mark as completed when navigating forward
        // Validation should happen in the form components
        const updatedSteps = [...steps];
        updatedSteps[currentStep] = { ...currentStepData, isCompleted: true };
        setSteps(updatedSteps);
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      const result = await createIndividualCustomer(
        Number(categoryId),
        formData
      );

      if (result.success) {
        message.success(result.message);
        // Navigate back to category selection after successful submission
        navigate('/customers/onboarding');
        resetOnboarding();
      } else {
        message.error(result.message);
      }
    } catch (error) {
      message.error('Failed to create customer. Please try again.');
      console.error('Submission error:', error);
    }
  };

  const handleCancel = () => {
    navigate('/customer-onboarding');
    resetOnboarding();
  };

  if (categoryLoading || fieldsLoading) {
    return (
      <div className="onboarding-loading">
        <Spin size="large" />
        <Paragraph>Loading onboarding configuration...</Paragraph>
      </div>
    );
  }

  if (categoryError || fieldsError || !category) {
    return (
      <div className="onboarding-error">
        <Alert
          message="Configuration Error"
          description="Unable to load onboarding configuration for this category."
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => navigate('/customer-onboarding')}>
              Back to Categories
            </Button>
          }
        />
      </div>
    );
  }

  // Render inline mode (all groups on one page with dividers)
  if (renderingMode === 'inline') {
    return (
      <div className="individual-onboarding-view">
        <div className="onboarding-header">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/customer-onboarding')}
            className="back-button"
          >
            Back to Categories
          </Button>
          
          <div className="category-info">
            <Title level={3}>{category.name}</Title>
            <Paragraph>{category.description}</Paragraph>
          </div>
        </div>

        <Card>
          {fieldConfiguration && (
            <InlineFieldGroups
              fieldConfiguration={fieldConfiguration}
              formData={{
                ...useOnboardingStore.getState().formData.customerData,
                ...useOnboardingStore.getState().formData.custInfoData,
              }}
              onFieldChange={(fieldName, value) => {
                useOnboardingStore.getState().updateFieldValue(
                  CUSTINFO_FIELDS.has(fieldName) ? 'custInfoData' : 'customerData',
                  fieldName,
                  value,
                );
              }}
              errors={{}}
              readonly={false}
            />
          )}
          
          {/* Action buttons for inline mode */}
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type="primary" onClick={handleSubmit}>
              Submit
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Render wizard mode (step-by-step with horizontal or vertical orientation)
  return (
    <div className="individual-onboarding-view">
      <div className="onboarding-header" style={{ marginBottom: '24px' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/customer-onboarding')}
          className="back-button"
        >
          Back to Categories
        </Button>
        
        <div className="category-info">
          <Title level={3}>{category.name}</Title>
          <Paragraph>{category.description}</Paragraph>
        </div>
      </div>

      <OnboardingWizard
        steps={steps}
        currentStep={currentStep}
        onStepChange={handleStepChange}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        canProceed={true}
        isSubmitting={isSubmitting}
        orientation={wizardOrientation as 'horizontal' | 'vertical'}
        showProgress={showProgress}
      >
        <IndividualOnboardingForm
          categoryId={Number(categoryId)}
          currentStep={currentStep}
          fieldConfiguration={fieldConfiguration}
          categoryProducts={category?.products || []}
          categoryLocations={category?.locations || []}
          categoryDocuments={category?.documents || []}
        />
      </OnboardingWizard>
    </div>
  );
};

export default IndividualOnboardingView;
